import { Platform } from '@angular/cdk/platform';
import {
	ChangeDetectionStrategy,
	Component,
	ComponentRef,
	computed,
	DestroyRef,
	Directive,
	effect,
	forwardRef,
	inject,
	Injector,
	input,
	output,
	signal,
	viewChild,
	ViewContainerRef,
	ViewEncapsulation,
} from '@angular/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { isUndefined } from '@skylab-shared/utils';
import { FilterDrawerComponent } from '../filtering/filter-drawer/filter-drawer.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { FilterChipContext, FilterChipStrategy } from './filter-chip/filter-chip-context';
import { Chip } from './filter-chips';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';

export interface FilterChipsOptions {
	chips: Chip[];
}

type ChipInstance = {
	decorator: ComponentRef<FilterChipDecoratorComponent>;
	content: ComponentRef<any>;
};

@Component({
	selector: 'skl-filter-chip-wrapper',
	template: `
		<ng-container #contentContainer />
		<button 
			class="remove-btn" 
			(click)="remove.emit()"
			[tooltip]="'ui.filter-chip.clear-button-label' | translate"
		>
			<skl-icon name="arrow-reset-filled" size="16px" />
		</button>
	`,
	styles: `
		skl-filter-chip-wrapper {
			--_surface: var(--surface-3-color);
			--_border-color: var(--line-1-color);

			font-size: 0.75rem;
			display: flex;
			align-items: center;
			background-color: var(--_surface);
			border: 1px solid var(--_border-color);
			border-radius: 0.75rem;
			align-items: stretch;
			height: fit-content;
			overflow: clip;
			transition: all 0.2s ease;

			&:focus-within:has(:focus-visible) {
				--form-frame-color: transparent;
				
				outline-offset: .1875rem;
				outline: 2px solid var(--primary-color-light);
			}

			&:hover {
				--_surface: color-mix(in oklch, var(--surface-3-color), light-dark(black, white) 15%);
			}

			& > *:first-child {
				width: 100%;
				margin-inline: var(--gutter-small);

			}

			&:has(> .has-value) {
				--_surface: color-mix(in oklch, var(--surface-4-color), light-dark(black, white) 10%);
				
				.remove-btn {
					display: grid;
				}
			}
			
			.form-field--frame {
				--surface-1-color: var(--_surface);
				--icon-color: var(--text-color);

				all: unset;
				align-items: center;

				&::part(select-value) {
		
					width: stretch;
					
				}

				.frame--outlined {
					border: none;
					padding: unset;
					align-items: center !important;
				}

				.subscript {
					padding-top: 0;
				}
			}

			.remove-btn {
				display: none;
				width: 2rem;
				border-radius: .6875rem;
				place-items: center;
				color: var(--text-color);
				transition: all 0.2s ease;
				cursor: pointer;
	
				&:hover, 
				&:focus-visible {
					background-color: color-mix(in oklch, var(--_surface), light-dark(black, white) 40%);
				}
			}
		}
	`,
	imports: [TooltipDirective, TranslatePipe, IconComponent],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterChipDecoratorComponent {
	public readonly remove = output();
	public readonly contentContainer = viewChild.required('contentContainer', { read: ViewContainerRef });
}

@Directive({
	selector: '[sklFilterChipsDrawer]',
})
export class FilterChipsDrawerDirective {
	constructor() {
		const host = inject(FilterChipsComponent);
		const vcr = inject(ViewContainerRef);
		host.drawerContainer.set(vcr);

		inject(DestroyRef).onDestroy(() => {
			if (host.drawerContainer() === vcr) {
				host.drawerContainer.set(null);
			}
		});
	}
}

@Component({
	selector: 'skl-filter-chips',
	template: `
		@if (mainChips().length > 0) {
			<div class="chips-wrapper">
				<ng-container #chipsContainer />
			</div>
		}

		@if (showDrawerButton()) {
			<skl-button 
				(click)="filterDrawer.open()" 
				class="add-chip-btn" 
				typeName="frame"
				[tooltip]="'ui.filter-chip.add-button-label' | translate"
				[attr.data-notification-badge]="badgeCount() || ''"
			>
				{{ 'ui.filter-chip.add-button-label' | translate }}
				<skl-icon name="filter-regular" size="20px" />
			</skl-button>
		}

		<skl-filter-drawer #filterDrawer (resetAll)="resetAll()">
			<ng-container sklFilterChipsDrawer />
		</skl-filter-drawer>
	`,
	styles: `
		:host {
			display: flex;
		}

		.chips-wrapper {
			display: flex;
			flex-wrap: wrap;
			gap: var(--gutter-small);
			margin-block: var(--gutter-small);

			&:first-child {
				flex: 1;
			}
		}

		.add-chip-btn {
			flex: 0 0 auto;
			height: fit-content;
			align-self: end;
			margin-block: var(--gutter-small);
		}
	`,
	providers: [
        FilterChipContext,
    ],
	imports: [TranslatePipe, TooltipDirective, FilterChipsDrawerDirective, forwardRef(() => FilterDrawerComponent), ButtonComponent, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterChipsComponent {
	readonly #platform = inject(Platform);
	readonly #injector = inject(Injector);
	readonly #destroyRef = inject(DestroyRef);	
	readonly #filterChipContext = inject(FilterChipContext);

	public readonly options = input.required<FilterChipsOptions>();
	public readonly showAll = input<boolean>(false);

	public readonly chipsContainer = viewChild('chipsContainer', { read: ViewContainerRef });
	
	readonly #mainChipInstances = new Map<string, ChipInstance>();
	readonly #drawerChipInstances = new Map<string, ChipInstance>();
	readonly #chipComponents = new Map<string, ComponentRef<any>[]>();
	readonly #wrappedChips = new WeakSet<Chip>();
	
	readonly #mainChipsLimit = 4 as const;

	public readonly drawerContainer = signal<ViewContainerRef | null>(null);
	public readonly chips = signal<Chip[] | null>(null);
	public readonly hiddenChips = computed(() => this.chips()?.filter(c => c.isHidden) || []);
	public readonly activeChips = computed(() => this.chips()?.filter(c => !c.isHidden) || []);
	public readonly mainChips = computed(() => {
		const chips = this.chips();
		if (this.showAll()) {
			return chips || [];
		}

		return chips?.slice(0, this.#mainChipsLimit) || [];
	});

	public readonly drawerChips = computed(() => this.chips() || []);
	public readonly badgeCount = computed(() => {
		const chips = this.chips();
		if (!chips) return 0;

		return chips.filter(c => this.#hasValue(c)).length;
	});

	public readonly isCompactView = computed(() => this.#platform.ANDROID || this.#platform.IOS);
	public readonly showDrawerButton = computed(() => !this.showAll() && (this.drawerChips().length > this.#mainChipsLimit || this.isCompactView()));

	public constructor() {
		effect(() => {
			const options = this.options();
			const container = this.chipsContainer();
			const drawerContainer = this.drawerContainer();
			const mainChips = this.mainChips();
			const drawerChips = this.drawerChips();

			if (!options) return;

			options.chips.forEach(c => this.#wrapChipOnChange(c));
			if (this.chips() !== options.chips) {
				this.chips.set(options.chips);
			}

			if (container) {
				this.#chipComponents.clear();
				this.#reconcile(container, mainChips, this.#mainChipInstances);
				
				if (drawerContainer) {
					this.#reconcile(drawerContainer, drawerChips, this.#drawerChipInstances);
				} else {
					this.#drawerChipInstances.forEach(inst => inst.decorator.destroy());
					this.#drawerChipInstances.clear();
				}
			}
		});

		this.#destroyRef.onDestroy(() => {
			this.#destroyChips();
			this.chips.set(null);
		});
	}


	public clearChip(chip: Chip) {
		const refs = this.#chipComponents.get(chip.identityName);
		if (refs && refs.length > 0) {
			(refs[0].instance as FilterChipStrategy).resetDefaultValue();
		} else {
			chip.value = !isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue;
			chip.onChange(chip.value);
		}
		this.#renderActiveChips();
	}

	public resetAll() {
		this.chips()?.forEach(chip => {
			const refs = this.#chipComponents.get(chip.identityName);
			if (refs && refs.length > 0) {
				(refs[0].instance as FilterChipStrategy).resetDefaultValue();
			} else {
				chip.value = !isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue;
				chip.onChange(chip.value);
			}
		});
		this.#renderActiveChips();
	}

	#renderActiveChips() {
		const container = this.chipsContainer();
		if (!container) return;

		this.#chipComponents.clear();

		this.#reconcile(container, this.mainChips(), this.#mainChipInstances);
		
		const drawerContainer = this.drawerContainer();
		if (drawerContainer) {
			this.#reconcile(drawerContainer, this.drawerChips(), this.#drawerChipInstances);
		} else {
			this.#drawerChipInstances.forEach(inst => inst.decorator.destroy());
			this.#drawerChipInstances.clear();
		}
	}

	#destroyChips() {
		this.#mainChipInstances.forEach(inst => inst.decorator.destroy());
		this.#mainChipInstances.clear();
		this.#drawerChipInstances.forEach(inst => inst.decorator.destroy());
		this.#drawerChipInstances.clear();
		this.#chipComponents.clear();
	}

	#hasValue(chip: Chip) {
		const defaultValue = !isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue;
		return this.#filterChipContext.compareValues(chip.value, defaultValue);
	}

	#wrapChipOnChange(chip: Chip) {
		if (this.#wrappedChips.has(chip)) return;

		if (isUndefined(chip.initialChipValue)) {
			chip.initialChipValue = chip.value;
		}

		const original = chip.onChange;
		chip.onChange = (value: unknown) => {
			chip.value = value;
			original(value);
			this.#syncChipInstances(chip);
			this.chips.update(chips => chips ? [...chips] : null);
		};
		this.#wrappedChips.add(chip);
	}

	#syncChipInstances(chip: Chip) {
		const refs = this.#chipComponents.get(chip.identityName);
		if (refs) {
			for (const ref of refs) {
				const strategy = ref.instance as FilterChipStrategy;
				strategy.setInitialValue();
			}
		}
	}

	#reconcile(container: ViewContainerRef, chips: Chip[], instancesMap: Map<string, ChipInstance>) {
		const newIds = new Set(chips.map(c => c.identityName));
		this.#cleanupRemovedInstances(instancesMap, newIds);

		for (const chip of chips) {
			const instance = this.#updateOrCreateInstance(container, chip, instancesMap);
			this.#registerChipComponent(chip.identityName, instance.content);
		}
	}

	#cleanupRemovedInstances(instancesMap: Map<string, ChipInstance>, newIds: Set<string>) {
		instancesMap.forEach((inst, id) => {
			if (!newIds.has(id)) {
				inst.decorator.destroy();
				instancesMap.delete(id);
			}
		});
	}

	#updateOrCreateInstance(container: ViewContainerRef, chip: Chip, instancesMap: Map<string, ChipInstance>): ChipInstance {
		let instance = instancesMap.get(chip.identityName);

		if (instance) {
			instance.content.setInput('chip', chip);
			(instance.content.instance as FilterChipStrategy).refresh();
		} else {
			instance = this.#createInstance(container, chip);
			instancesMap.set(chip.identityName, instance);
		}

		return instance;
	}

	#createInstance(container: ViewContainerRef, chip: Chip): ChipInstance {
		const decoratorRef = container.createComponent(FilterChipDecoratorComponent, { injector: this.#injector });
		const decoratorSub = decoratorRef.instance.remove.subscribe(() => this.clearChip(chip));
		decoratorRef.onDestroy(() => decoratorSub.unsubscribe());

		const contentRef = decoratorRef.instance.contentContainer().createComponent(chip.component, { injector: this.#injector });
		contentRef.setInput('chip', chip);

		if ((contentRef.instance as any).remove) {
			const contentSub = (contentRef.instance as any).remove.subscribe(() => this.clearChip(chip));
			contentRef.onDestroy(() => contentSub.unsubscribe());
		}

		const element = decoratorRef.location.nativeElement;
		if (element?.setAttribute) {
			element.setAttribute('id', chip.identityName);
		}

		return {
			decorator: decoratorRef,
			content: contentRef
		};
	}

	#registerChipComponent(identityName: string, contentRef: ComponentRef<any>) {
		const refs = this.#chipComponents.get(identityName) || [];
		refs.push(contentRef);
		this.#chipComponents.set(identityName, refs);
	}
}
